import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, TableCell, TableRow, Typography } from "@mui/material";
import dayjs from "dayjs";

import CommonButton from "../../../component/button/Button";
import addCircleIcon from "../../../assets/icons/add-circle.svg";
import {
  formatAmount,
  formatGermanAmount,
  formatPlainAmount,
  formatUpdateNumber,
} from "../../../helper/formatAmount";
import TableComponent from "../../../component/Table/TableComponent";
import Toast from "../../../component/toastify/Index";
import editIcon from "../../../assets/icons/Edit.svg";
import deleteIcon from "../../../assets/icons/trash.svg";
import {
  createKostenplanung,
  deleteKostenplanung,
  getByIdKostenplanung,
  getKostenplanungLists,
  updateKostenplanung,
  loadVerbindlichkeitenOptions,
} from "../../../services/customer-services/customerKostenplanungService";
import GeneratePDF from "../../../component/PDFGenerator";
import KostenplanungModal from "../../customer/umsatzplanung/KostenplanungModal";

const columns = [
  { field: "kunde", headerName: "Kunde", translationKey: "forderungen_kunde" },
  {
    field: "nettoBetrag",
    headerName: "Netto Betrag",
    translationKey: "forderungen_nettoBetrag",
  },
  {
    field: "bruttoBetrag",
    headerName: "Brutto Betrag",
    translationKey: "forderungen_bruttoBetrag",
  },
  {
    field: "ausVorwoche",
    headerName: "AUS Vorwoche",
    translationKey: "umsatzplanung_aus_vorwoche",
  },
  {
    field: "bemerkung",
    headerName: "Bemerkung",
    translationKey: "umsatzplanung_bemerkung",
  },
  {
    field: "action",
    headerName: "Aktion",
    translationKey: "client_list_action",
  },
];

const Kostenplanung = ({
  shouldDisableDate,
  selectedWeek,
  setTotals,
  clientId,
}) => {
  const { t } = useTranslation();
  const [kostenplanungs, setKostenplanungs] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    kunde: "",
    rechnungsNummer: null,
    datum: null,
    nettoBetrag: "",
    umsatzsteuer: "",
    steuer: "",
    bruttoBetrag: "",
    rechnungsart: "",
    bemerkung: "",
    ausVorwoche: "",
  });
  const [errors, setErrors] = useState({});
  const [debouncedNettoBetrag, setDebouncedNettoBetrag] = useState(
    formData?.nettoBetrag || "",
  );
  const [timer, setTimer] = useState(null);
  const [options, setOptions] = useState([]);
  const [verbindlichkeitenOptions, setVerbindlichkeitenOptions] = useState([]);

  const parseAmount = (str) => {
    if (!str) return NaN;

    // Remove spaces
    str = str.trim();

    // If comma exists and dot exists
    if (str.includes(",") && str.includes(".")) {
      // German format: "1.234,56"
      return parseFloat(str.replace(/\./g, "").replace(",", "."));
    }
    // Only comma exists: could be German decimal "1234,56"
    else if (str.includes(",") && !str.includes(".")) {
      return parseFloat(str.replace(",", "."));
    }
    // Dot exists or nothing else: English format "1234.56" or "1234"
    else {
      return parseFloat(str);
    }
  };

  useEffect(() => {
    if (!debouncedNettoBetrag || !formData.umsatzsteuer) return;

    const netto = parseAmount(debouncedNettoBetrag);
    const steuerPercent = parseFloat(formData.umsatzsteuer);

    if (!isNaN(netto) && !isNaN(steuerPercent)) {
      const steuerAmount = (netto * steuerPercent) / 100;
      setFormData((prev) => ({
        ...prev,
        steuer: formatAmount(steuerAmount),
        bruttoBetrag: formatAmount(netto + steuerAmount),
      }));
    }
  }, [debouncedNettoBetrag, formData.umsatzsteuer]);

  const getKostenplanung = async () => {
    if (clientId) {
      const payload = {
        pageNo,
        perPage,
        year: selectedWeek?.year,
        weekNumber: selectedWeek?.weekNumber,
        clientId,
      };
      const response = await getKostenplanungLists(payload);
      if (response?.isSuccess) {
        setKostenplanungs([...response.data]);
        setTotalRecords(response.totalRecord);
        const totalNetto = response.data?.reduce(
          (sum, invoice) => sum + Number(invoice?.nettoBetrag),
          0,
        );
        const totalBrutto = response.data?.reduce(
          (sum, invoice) => sum + Number(invoice?.bruttoBetrag),
          0,
        );
        setTotals((prevTotals) => ({
          ...prevTotals,
          kostenplanungNetto: totalNetto,
          kostenplanungBrutto: totalBrutto,
        }));
      } else {
        setKostenplanungs([]);
        setTotalRecords(0);
        setTotals((prevTotals) => ({
          ...prevTotals,
          kostenplanungNetto: 0,
          kostenplanungBrutto: 0,
        }));
      }
    }
  };

  useEffect(() => {
    getKostenplanung();
  }, [pageNo, perPage, selectedWeek, clientId]);

  const getverbindlichkeitenOptions = async () => {
    if (clientId) {
      const response = await loadVerbindlichkeitenOptions(clientId);
      if (response?.isSuccess) {
        setOptions([...response.data]);
        setVerbindlichkeitenOptions([...response.data]);
      } else {
        setOptions([]);
        setVerbindlichkeitenOptions([]);
      }
    }
  };

  useEffect(() => {
    getverbindlichkeitenOptions();
  }, []);

  // Handle date change
  const handleDateChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  useEffect(() => {
    const debounceTime = setTimeout(() => {
      setFormData((prev) => ({ ...prev, nettoBetrag: debouncedNettoBetrag }));
    }, 1000);
    return () => clearTimeout(debounceTime);
  }, [debouncedNettoBetrag]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "nettoBetrag") {
      // Immediately set the raw value in the form state to avoid losing user input while typing
      setFormData((prev) => ({ ...prev, [name]: value }));

      // Clear the previous timer if it's still active
      if (timer) {
        clearTimeout(timer);
      }

      // Set a new timer for debouncing the formatted value update
      const newTimer = setTimeout(() => {
        // Format the value only after the debounce delay
        const formattedValue = formatGermanAmount(value);
        setDebouncedNettoBetrag(formattedValue); // Update debounced value after the delay
      }, 500); // 500 ms debounce delay

      setTimer(newTimer);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.kunde) {
      newErrors.kunde = `${t("forderungen_kunde")} ${t("is_required")}`;
    }

    if (!formData.rechnungsNummer) {
      newErrors.rechnungsNummer = `${t("forderungen_rechnungsNummer")} ${t(
        "is_required",
      )}`;
    }

    if (!formData.datum) {
      newErrors.datum = `${t("umsatzplanung_datum")} ${t("is_required")}`;
    }

    if (!formData.nettoBetrag) {
      newErrors.nettoBetrag = `${t("forderungen_nettoBetrag")} ${t(
        "is_required",
      )}`;
    } else {
      const germanNumber = formData?.nettoBetrag
        ?.replace(/\./g, "")
        ?.replace(/,/g, ".");
      const nettoValue = Number(germanNumber);

      if (isNaN(nettoValue) || !Number.isFinite(nettoValue)) {
        newErrors.nettoBetrag = t("verbindlicheitem_netto_betrag_valid_number");
      } else if (nettoValue <= 0) {
        newErrors.nettoBetrag = `${t("forderungen_nettoBetrag")} ${t(
          "cannot_be_negative",
        )}`;
      }
    }

    if (!formData.umsatzsteuer) {
      newErrors.umsatzsteuer = `${t("forderungen_umsatzsteuer")} ${t(
        "is_required",
      )}`;
    }

    if (!formData.bemerkung) {
      newErrors.bemerkung = `${t("umsatzplanung_bemerkung")} ${t(
        "is_required",
      )}`;
    }

    if (!formData.ausVorwoche) {
      newErrors.ausVorwoche = `${t("umsatzplanung_aus_vorwoche")} ${t(
        "is_required",
      )}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const toCleanNumberString = (value) => {
    if (value == null) return "0";
    const str = String(value);
    const num = Number(str?.replace(/\./g, "")?.replace(",", "."));
    return String(num);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const datum = dayjs(formData?.datum).format("DD-MM-YYYY");
    const nettoBetrag = toCleanNumberString(formatAmount(formData?.nettoBetrag));
    const monthNumber = dayjs(formData?.datum).month() + 1;
    const year = dayjs(formData?.datum).year();
    const steuer = toCleanNumberString(formData?.steuer);
    const bruttoBetrag = toCleanNumberString(formData?.bruttoBetrag);

    const payload = {
      year: selectedWeek?.year,
      weekNumber: selectedWeek?.weekNumber,
      startDate: dayjs(selectedWeek?.weekStart).format("DD-MM-YYYY"),
      endDate: dayjs(selectedWeek?.weekEnd).format("DD-MM-YYYY"),
      kostenplanung: {
        ...formData,
        datum,
        steuer,
        bruttoBetrag,
        nettoBetrag,
        calendarMonth: monthNumber,
        kundeModel: "Verbindlichkeiten",
        calendarYear: year,
      },
      clientId,
    };

    let response = [];
    if (editId) {
      response = await updateKostenplanung({
        ...payload,
        id: editId,
      });
    } else {
      response = await createKostenplanung({
        ...payload,
      });
    }
    if (response?.isSuccess) {
      Toast(response?.message, "success");
      setIsModalOpen(false);
      getKostenplanung();
      setFormData({
        kunde: "",
        rechnungsNummer: null,
        datum: null,
        nettoBetrag: "",
        umsatzsteuer: "",
        steuer: "",
        bruttoBetrag: "",
        rechnungsart: "",
        bemerkung: "",
        ausVorwoche: "",
      });
      setDebouncedNettoBetrag("");
      setTimer(null);
      setEditId(null);
      setVerbindlichkeitenOptions(verbindlichkeitenOptions.filter((opt) => formData.kunde !== opt.value));
    }
  };

  const handleEdit = async (editId) => {
    const response = await getByIdKostenplanung(editId, clientId);
    setFormData({
      ...response.data,
      datum: response.data?.datum ? dayjs(response.data?.datum) : null,
      nettoBetrag: formatUpdateNumber(Number(response.data?.nettoBetrag)),
      bruttoBetrag: formatUpdateNumber(Number(response.data?.bruttoBetrag)),
      steuer: formatUpdateNumber(Number(response.data?.steuer)),
    });
    setDebouncedNettoBetrag(
      formatUpdateNumber(Number(response.data?.nettoBetrag)),
    );
    setEditId(editId);
    setIsModalOpen(true);
    setErrors({});
  };

  const handleDelete = async (deleteId) => {
    const response = await deleteKostenplanung(deleteId, clientId);
    if (response?.isSuccess) {
      Toast(response?.message, "success");
      getKostenplanung();
      getverbindlichkeitenOptions();
    }
  };

  const handleAdd = () => {
    setIsModalOpen(true);
  };

  const totalNetto = kostenplanungs?.reduce(
    (sum, invoice) => sum + Number(invoice?.nettoBetrag),
    0,
  );
  const totalBrutto = kostenplanungs?.reduce(
    (sum, invoice) => sum + Number(invoice?.bruttoBetrag),
    0,
  );

  // Generate PDF
  const excludedKeys = [
    "_id",
    "createdAt",
    "updatedAt",
    "isDeleted",
    "__v",
    "rechnungsart",
    "company",
    "datum",
    "customers",
    "startDate",
    "steuer",
    "kostenplanungId",
    "umsatzsteuer",
    "weekNumber",
    "year",
    "endDate",
    "rechnungsNummer",
  ];

  const customLabels = {
    kunde: "Kunde",
    nettoBetrag: "Netto Betrag",
    bruttoBetrag: "Brutto Betrag",
    ausVorwoche: "AUS Vorwoche",
    bemerkung: "Bemerkung",
  };

  const customColumnOrder = [
    "kunde",
    "nettoBetrag",
    "bruttoBetrag",
    "ausVorwoche",
    "bemerkung",
  ];

  const formatCell = (key, value) => {
    if (["bruttoBetrag", "nettoBetrag"]?.includes(key)) {
      return formatAmount(value) + " €";
    }

    return value || "-";
  };

  return (
    <Box
      sx={{
        paddingTop: 1,
      }}
    >
      <KostenplanungModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        formData={formData}
        setFormData={setFormData}
        setDebouncedNettoBetrag={setDebouncedNettoBetrag}
        setTimer={setTimer}
        setEditId={setEditId}
        setErrors={setErrors}
        errors={errors}
        t={t}
        options={options}
        handleSubmit={handleSubmit}
        handleChange={handleChange}
        handleDateChange={handleDateChange}
        shouldDisableDate={shouldDisableDate}
        selectedWeek={selectedWeek}
        formatAmount={formatAmount}
      />
      <Box
        sx={{
          px: 2,
          pt: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start" },
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {t("umsatzplanung_kostenplanung")}
        </Typography>
        <CommonButton
          text={`${t("umsatzplanung_verb")}`}
          onClick={handleAdd}
          icon={
            <img
              src={addCircleIcon}
              alt="addCircleIcon"
              className="button-icon-padding"
            />
          }
          className="add-user-btn button-border-radius"
        />
      </Box>
      <Box
        sx={{
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <TableComponent
          data={kostenplanungs}
          setPageNo={setPageNo}
          pageNo={pageNo}
          perPage={perPage}
          setPerPage={setPerPage}
          totalRecords={totalRecords}
          columns={columns}
          className={"table-max-height"}
        >
          {kostenplanungs?.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              <TableCell>{row.kunde}</TableCell>
              <TableCell>{formatAmount(row.nettoBetrag) + " €"}</TableCell>
              <TableCell>{formatAmount(row.bruttoBetrag) + " €"}</TableCell>
              <TableCell>
                {row?.ausVorwoche
                  ? t(`common_${row?.ausVorwoche?.toLowerCase()}`)
                  : "-"}
              </TableCell>
              <TableCell>{row?.bemerkung}</TableCell>
              <TableCell>
                <Typography
                  className="action"
                  classes="flex"
                  alignItems={"center"}
                  display={"flex"}
                >
                  <span onClick={() => handleEdit(row.kostenplanungId)}>
                    <img src={editIcon} alt="edit icon" className="edit-icon" />
                  </span>
                  <span onClick={() => handleDelete(row.kostenplanungId)}>
                    <img src={deleteIcon} alt="delete icon" />
                  </span>
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableComponent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            marginTop: "1.75rem",
          }}
        >
          <Typography>
            {t("common_sum")} ({t("forderungen_netto")}):{" "}
            {formatAmount(totalNetto) + " €"}
          </Typography>
          <Typography>
            {t("common_sum")} ({t("forderungen_brutto")}):{" "}
            {formatAmount(totalBrutto) + " €"}
          </Typography>
          <GeneratePDF
            data={kostenplanungs}
            excludedKeys={excludedKeys}
            customLabels={customLabels}
            customColumnOrder={customColumnOrder}
            customRowsPerPage={30}
            formatCell={formatCell}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Kostenplanung;
